// FLEX DSL API Service
// Handles all API communication for FLEX scripts

const API_BASE = '/api/flex';

class FlexService {
  /**
   * Get the current user ID from localStorage
   */
  static getCurrentUserId() {
    try {
      const userJson = localStorage.getItem('waide_user');
      if (userJson) {
        const user = JSON.parse(userJson);
        return user.id || user.email || null;
      }
    } catch (e) {
      console.error('Failed to get user ID:', e);
    }
    return null;
  }

  /**
   * Get auth headers for API requests
   */
  static getAuthHeaders() {
    const token = localStorage.getItem('waide_access_token');
    const userId = this.getCurrentUserId();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(userId && { 'X-User-Id': userId }),
    };
  }

  /**
   * Handle API response
   */
  static async handleResponse(response) {
    if (!response.ok) {
      const text = await response.text();
      let message = text || `HTTP error ${response.status}`;
      try {
        const data = JSON.parse(text);
        message = data.errorMessage || data.message || data.error?.message || data.error || message;
      } catch { /* Plain-text errors remain readable. */ }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  // ============ Script CRUD Operations ============

  /**
   * List all scripts
   * @param {Object} options - Filter options
   * @param {string} options.warehouseId - Filter by warehouse
   * @param {string} options.tenantId - Filter by tenant
   * @param {string} options.triggerType - Filter by trigger type
   */
  static async listScripts(options = {}) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.tenantId) params.append('tenantId', options.tenantId);
    if (options.triggerType) params.append('triggerType', options.triggerType);
    if (options.domain) params.append('domain', options.domain);
    if (options.category) params.append('category', options.category);

    const url = `${API_BASE}/scripts${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get scripts organized as a domain/category tree structure
   */
  static async getScriptTree(options = {}) {
    const params = new URLSearchParams();
    if (options.tenantId) params.append('tenantId', options.tenantId);
    if (options.connectedSystemId) params.append('connectedSystemId', options.connectedSystemId);

    const url = `${API_BASE}/scripts/tree${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Export a single script as a .flex file download (with YAML frontmatter)
   */
  static async exportScript(script) {
    const lines = ['---'];
    const addField = (key, value) => {
      if (value === null || value === undefined || value === '') return;
      const str = String(value);
      if (str.includes(':') || str.includes('#') || str.includes('"') || str.includes("'") || str.includes('\n')) {
        lines.push(`${key}: "${str.replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`);
      } else {
        lines.push(`${key}: ${str}`);
      }
    };
    addField('id', script.id);
    addField('name', script.name);
    addField('displayName', script.displayName);
    addField('version', script.version);
    addField('description', script.description);
    addField('domain', script.domain);
    addField('category', script.category);
    addField('triggerType', script.triggerType);
    addField('triggerConfig', script.triggerConfig);
    addField('extendsScript', script.extendsScript);
    addField('extendsScope', script.extendsScope);
    addField('isActive', script.isActive);
    addField('isLocked', script.isLocked);
    addField('createdBy', script.createdBy);
    lines.push('---');
    lines.push(script.scriptBody || '');

    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${script.name || 'script'}.flex`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Sync all scripts from DB to file system (backend/src/scripts/)
   */
  static async syncToFiles() {
    const response = await fetch(`${API_BASE}/scripts/sync-to-files`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Sync all scripts from file system into DB (returns per-file results)
   * @param {boolean} validate - If true, also parse each script and report syntax errors
   */
  static async syncFromFiles(validate = false) {
    const params = validate ? '?validate=true' : '';
    const response = await fetch(`${API_BASE}/scripts/sync-from-files${params}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Retry sync for a single .flex file (by relative path)
   */
  static async retrySyncFile(relativePath) {
    const response = await fetch(`${API_BASE}/scripts/sync-single-file`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: relativePath }),
    });
    return this.handleResponse(response);
  }

  /**
   * Read a .flex file content by relative path
   */
  static async readFileContent(relativePath) {
    const response = await fetch(`${API_BASE}/scripts/file-content/read`, {
      method: 'POST',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: relativePath }),
    });
    return this.handleResponse(response);
  }

  /**
   * Write .flex file content and re-sync to DB (returns updated sync result)
   */
  static async writeFileContent(relativePath, content) {
    const response = await fetch(`${API_BASE}/scripts/file-content`, {
      method: 'PUT',
      headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: relativePath, content }),
    });
    return this.handleResponse(response);
  }

  /**
   * Export all scripts as a ZIP download
   */
  static async exportAllAsZip() {
    const response = await fetch(`${API_BASE}/scripts/export-zip`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to export scripts');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flex-scripts.zip';
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Get a script by ID
   * @param {number} id - Script ID
   */
  static async getScript(id) {
    const response = await fetch(`${API_BASE}/scripts/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get an agent definition as raw file content
   */
  static async getAgent(id) {
    const response = await fetch(`${API_BASE}/scripts/agents/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get a workspace definition as raw file content
   */
  static async getWorkspace(id) {
    const response = await fetch(`${API_BASE}/scripts/workspaces/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Update an agent definition from raw file content
   */
  static async updateAgent(id, content) {
    const response = await fetch(`${API_BASE}/scripts/agents/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    return this.handleResponse(response);
  }

  /**
   * Update a workspace definition from raw file content
   */
  static async updateWorkspace(id, content) {
    const response = await fetch(`${API_BASE}/scripts/workspaces/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete an agent by ID
   */
  static async deleteAgent(id) {
    const response = await fetch(`${API_BASE}/scripts/agents/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete a workspace by ID
   */
  static async deleteWorkspace(id) {
    const response = await fetch(`${API_BASE}/scripts/workspaces/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create a new script
   * @param {Object} script - Script data
   */
  static async createScript(script) {
    const response = await fetch(`${API_BASE}/scripts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(script),
    });
    return this.handleResponse(response);
  }

  /**
   * Duplicate a script as a tenant- or system-scoped override (same name as
   * the source, scoped to the CURRENT user's tenant/connected system --
   * never a client-supplied id). scope is 'tenant' or 'system'.
   */
  static async duplicateScript(id, scope = 'tenant') {
    const response = await fetch(`${API_BASE}/scripts/${id}/duplicate?scope=${encodeURIComponent(scope)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      let message = 'Duplicate failed';
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch { /* body wasn't JSON -- keep default message */ }
      throw new Error(message);
    }
    return response.json();
  }

  /**
   * Update an existing script
   * @param {number} id - Script ID
   * @param {Object} script - Updated script data
   */
  static async updateScript(id, script) {
    const response = await fetch(`${API_BASE}/scripts/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(script),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete a script (soft delete)
   * @param {number} id - Script ID
   */
  static async deleteScript(id) {
    const response = await fetch(`${API_BASE}/scripts/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text || `HTTP error ${response.status}`;
      try {
        const data = JSON.parse(text);
        message = data.errorMessage || data.message || data.error?.message || data.error || message;
      } catch { /* Plain-text errors remain readable. */ }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return true;
  }

  /**
   * Permanently delete a script from DB and file system.
   * Returns { referencedBy: [...] } on conflict (409), or true on success.
   */
  static async permanentDeleteScript(id) {
    const response = await fetch(`${API_BASE}/scripts/${id}/permanent`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (response.status === 409) {
      const data = await response.json();
      return { conflict: true, referencedBy: data.referencedBy || [] };
    }
    if (!response.ok) {
      const text = await response.text();
      let message = text || `HTTP error ${response.status}`;
      try {
        const data = JSON.parse(text);
        message = data.errorMessage || data.message || data.error?.message || data.error || message;
      } catch { /* Plain-text errors remain readable. */ }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return { conflict: false };
  }

  /**
   * Toggle the lock status of a script
   * @param {string} id - Script ID
   */
  static async toggleLock(id) {
    const response = await fetch(`${API_BASE}/scripts/${id}/toggle-lock`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Script Execution ============

  /**
   * Execute a script
   * @param {Object} request - Execution request
   * @param {string} request.scriptBody - Script source code (for ad-hoc execution)
   * @param {number} request.scriptId - Script ID (for saved scripts)
   * @param {Object} request.context - Execution context variables
   * @param {string} request.executionMode - 'run' (default), 'tracked', or 'background'
   */
  static async executeScript(request) {
    const response = await fetch(`${API_BASE}/execute`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return this.handleResponse(response);
  }

  /**
   * Execute a script with task tracking (synchronous)
   * @param {Object} request - Execution request
   */
  static async executeScriptTracked(request) {
    return this.executeScript({ ...request, executionMode: 'tracked' });
  }

  /**
   * Execute a script in the background (async with task tracking)
   * Returns immediately with taskId
   * @param {Object} request - Execution request
   */
  static async executeScriptBackground(request) {
    return this.executeScript({ ...request, executionMode: 'background' });
  }

  /**
   * Execute a trigger-based script
   * @param {string} triggerType - Type of trigger (e.g., 'before_receiving', 'after_putaway')
   * @param {Object} context - Execution context
   */
  static async executeTrigger(triggerType, context = {}) {
    const response = await fetch(`${API_BASE}/trigger/${triggerType}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(context),
    });
    return this.handleResponse(response);
  }

  // ============ Script Validation ============

  /**
   * Parse/validate a script without executing
   * @param {string} scriptBody - Script source code
   */
  static async parseScript(scriptBody) {
    const response = await fetch(`${API_BASE}/parse`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ scriptBody }),
    });
    return this.handleResponse(response);
  }

  /**
   * Validate a script (alias for parseScript with better error handling)
   * @param {string} scriptBody - Script source code
   */
  static async validateScript(scriptBody) {
    const response = await fetch(`${API_BASE}/scripts/validate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ scriptBody }),
    });
    return this.handleResponse(response);
  }

  // ============ AI Generation (Phase 3) ============

  /**
   * Generate a FLEX script from natural language
   * @param {Object} request - Generation request
   * @param {string} request.prompt - Natural language description
   * @param {string} request.warehouseId - Warehouse context
   */
  static async generateScript(request) {
    const response = await fetch(`${API_BASE}/generate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return this.handleResponse(response);
  }

  // ============ Entity Registry ============

  /**
   * Get available entities and methods for autocomplete
   */
  static async getEntityRegistry() {
    const response = await fetch(`${API_BASE}/entities`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Registry Management ============

  /**
   * Get registry summary
   */
  static async getRegistrySummary() {
    const response = await fetch(`${API_BASE}/registry/summary`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List entities
   */
  static async listEntities(options = {}) {
    const params = new URLSearchParams();
    if (options.domain) params.append('domain', options.domain);
    if (options.category) params.append('category', options.category);
    if (options.activeOnly) params.append('activeOnly', 'true');

    const url = `${API_BASE}/registry/entities${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
    return this.handleResponse(response);
  }

  /**
   * Create/Update/Delete entity
   */
  static async saveEntity(entity) {
    const method = entity.id ? 'PUT' : 'POST';
    const url = entity.id ? `${API_BASE}/registry/entities/${entity.id}` : `${API_BASE}/registry/entities`;
    const response = await fetch(url, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(entity),
    });
    return this.handleResponse(response);
  }

  static async deleteEntity(id) {
    const response = await fetch(`${API_BASE}/registry/entities/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete entity');
    return true;
  }

  /**
   * List fields
   */
  static async listFields(entityName = null) {
    const params = entityName ? `?entityName=${entityName}` : '';
    const response = await fetch(`${API_BASE}/registry/fields${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create/Update/Delete field
   */
  static async saveField(field) {
    const method = field.id ? 'PUT' : 'POST';
    const url = field.id ? `${API_BASE}/registry/fields/${field.id}` : `${API_BASE}/registry/fields`;
    const response = await fetch(url, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(field),
    });
    return this.handleResponse(response);
  }

  static async deleteField(id) {
    const response = await fetch(`${API_BASE}/registry/fields/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete field');
    return true;
  }

  /**
   * List tools
   */
  static async listTools(options = {}) {
    const params = new URLSearchParams();
    if (options.domain) params.append('domain', options.domain);
    if (options.category) params.append('category', options.category);
    if (options.activeOnly) params.append('activeOnly', 'true');

    const url = `${API_BASE}/registry/tools${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
    return this.handleResponse(response);
  }

  /**
   * Create/Update/Delete tool
   */
  static async saveTool(tool) {
    const method = tool.toolId ? 'PUT' : 'POST';
    const url = tool.toolId ? `${API_BASE}/registry/tools/${tool.toolId}` : `${API_BASE}/registry/tools`;
    const response = await fetch(url, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(tool),
    });
    return this.handleResponse(response);
  }

  static async deleteTool(id) {
    const response = await fetch(`${API_BASE}/registry/tools/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete tool');
    return true;
  }

  /**
   * List actions (entity functions)
   */
  static async listActions(entityName = null) {
    const params = entityName ? `?entityName=${entityName}` : '';
    const response = await fetch(`${API_BASE}/registry/actions${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create/Update action
   */
  static async saveAction(action) {
    const method = action.id ? 'PUT' : 'POST';
    const url = action.id ? `${API_BASE}/registry/actions/${action.id}` : `${API_BASE}/registry/actions`;
    const response = await fetch(url, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(action),
    });
    return this.handleResponse(response);
  }

  static async deleteAction(id) {
    const response = await fetch(`${API_BASE}/registry/actions/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete action');
    return true;
  }

  /**
   * List APIs
   */
  static async listApis(options = {}) {
    const params = new URLSearchParams();
    if (options.domain) params.append('domain', options.domain);
    if (options.entityName) params.append('entityName', options.entityName);
    if (options.activeOnly) params.append('activeOnly', 'true');

    const url = `${API_BASE}/registry/apis${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
    return this.handleResponse(response);
  }

  /**
   * Create/Update/Delete API
   */
  static async saveApi(api) {
    const method = api.id ? 'PUT' : 'POST';
    const url = api.id ? `${API_BASE}/registry/apis/${api.id}` : `${API_BASE}/registry/apis`;
    const response = await fetch(url, {
      method,
      headers: this.getAuthHeaders(),
      body: JSON.stringify(api),
    });
    return this.handleResponse(response);
  }

  static async deleteApi(id) {
    const response = await fetch(`${API_BASE}/registry/apis/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      let message = 'Failed to delete API';
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch { /* body wasn't JSON -- keep default message */ }
      throw new Error(message);
    }
    return true;
  }

  /**
   * Ping an API to check if it's reachable
   * @param {string} id - API ID
   * @returns {Object} { success: boolean, responseTime?: number, error?: string }
   */
  static async pingApi(id) {
    const response = await fetch(`${API_BASE}/registry/apis/${id}/ping`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Task Management ============

  /**
   * List all tasks (running and recent)
   * @param {Object} options - Filter options
   * @param {string} options.status - Filter by status (PENDING, RUNNING, COMPLETED, FAILED, CANCELLED)
   * @param {string} options.userId - Filter by user
   * @param {string} options.warehouseId - Filter by warehouse
   */
  static async listTasks(options = {}) {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.userId) params.append('userId', options.userId);
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    // FH-272: the background runs started from chat (all of the user's, or one chat's with sessionId)
    if (options.background) params.append('background', 'true');
    if (options.sessionId) params.append('sessionId', options.sessionId);

    const url = `${API_BASE}/tasks${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
    return this.handleResponse(response);
  }

  /**
   * Get running tasks only
   */
  static async listRunningTasks() {
    const response = await fetch(`${API_BASE}/tasks/running`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get task statistics
   */
  static async getTaskStats() {
    const response = await fetch(`${API_BASE}/tasks/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get task details by ID
   * @param {string} taskId - Task ID
   */
  static async getTask(taskId) {
    const response = await fetch(`${API_BASE}/tasks/${taskId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Cancel a running task
   * @param {string} taskId - Task ID
   * @param {string} reason - Cancellation reason
   */
  static async cancelTask(taskId, reason = 'Cancelled by user') {
    const response = await fetch(`${API_BASE}/tasks/${taskId}/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ reason, cancelledBy: this.getCurrentUserId() }),
    });
    return this.handleResponse(response);
  }

  /**
   * Remove a completed task from the registry
   * @param {string} taskId - Task ID
   */
  static async removeTask(taskId) {
    const response = await fetch(`${API_BASE}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Cache Management ============

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics with hit/miss rates
   */
  static async getCacheStats() {
    const response = await fetch(`${API_BASE}/cache/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Evict all FLEX caches
   * @returns {Object} Confirmation message
   */
  static async evictAllCaches() {
    const response = await fetch(`${API_BASE}/cache/evict`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Evict script caches only (resolution + parsed)
   * @returns {Object} Confirmation message
   */
  static async evictScriptCaches() {
    const response = await fetch(`${API_BASE}/cache/evict/scripts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Evict entity function cache only
   * @returns {Object} Confirmation message
   */
  static async evictEntityCaches() {
    const response = await fetch(`${API_BASE}/cache/evict/entities`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ============ Connected Systems (foysonis_fast_secure schema) ============

  /**
   * List all connected systems
   */
  static async listConnectedSystems() {
    const response = await fetch('/api/v1/connected-systems', {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get a connected system by ID
   * @param {string} id - System ID
   */
  static async getConnectedSystem(id) {
    const response = await fetch(`/api/v1/connected-systems/${id}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create a new connected system
   * @param {Object} system - System data
   */
  static async createConnectedSystem(system) {
    const response = await fetch('/api/v1/connected-systems', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(system),
    });
    return this.handleResponse(response);
  }

  /**
   * Update a connected system
   * @param {string} id - System ID
   * @param {Object} system - Updated system data
   */
  static async updateConnectedSystem(id, system) {
    const response = await fetch(`/api/v1/connected-systems/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(system),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete a connected system
   * @param {string} id - System ID
   */
  static async deleteConnectedSystem(id) {
    const response = await fetch(`/api/v1/connected-systems/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Test connection to a connected system
   * @param {string} id - System ID
   */
  static async testConnectedSystem(id) {
    const response = await fetch(`/api/v1/connected-systems/${id}/test`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * List endpoints for a connected system
   * @param {string} systemId - System ID
   */
  static async listConnectedSystemEndpoints(systemId) {
    const response = await fetch(`/api/v1/connected-systems/${systemId}/endpoints`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create endpoint for a connected system
   * @param {string} systemId - System ID
   * @param {Object} endpoint - Endpoint data
   */
  static async createConnectedSystemEndpoint(systemId, endpoint) {
    const response = await fetch(`/api/v1/connected-systems/${systemId}/endpoints`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(endpoint),
    });
    return this.handleResponse(response);
  }

  /**
   * Update endpoint
   * @param {string} systemId - System ID
   * @param {string} endpointId - Endpoint ID
   * @param {Object} endpoint - Updated endpoint data
   */
  static async updateConnectedSystemEndpoint(systemId, endpointId, endpoint) {
    const response = await fetch(`/api/v1/connected-systems/${systemId}/endpoints/${endpointId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(endpoint),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete endpoint
   * @param {string} systemId - System ID
   * @param {string} endpointId - Endpoint ID
   */
  static async deleteConnectedSystemEndpoint(systemId, endpointId) {
    const response = await fetch(`/api/v1/connected-systems/${systemId}/endpoints/${endpointId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }
}

export default FlexService;
